import { createRouter, createWebHistory } from 'vue-router'
import Home from '../views/Home.vue'
import { useAuthStore } from '../stores/auth.store'
import {
    forceReloadDueToStaleAssets,
    isStaleModuleImportError,
} from '@/utils/stale-client.js'
import { trackEvent } from '@/utils/tracking.js'
import firebase from 'firebase/compat/app'
import * as authUtils from '@/utils/auth.js'
import {
    safeLocalStorageGet,
    safeLocalStorageRemove,
    safeLocalStorageSet,
} from '@/utils/storage.js'
import { detectNavigatorLocale, loadLocaleResources } from '@/i18n.js'

async function waitForAppInitialization(authStore, timeoutMs = 5000) {
    const startTime = Date.now()
    while (
        !authStore.state.appInitialized &&
        Date.now() - startTime < timeoutMs
    ) {
        await new Promise((resolve) => setTimeout(resolve, 100))
    }

    if (!authStore.state.appInitialized) {
        console.warn(
            `waitForAppInitialization timed out after ${timeoutMs}ms, proceeding anyway`
        )
    }
}

function syncUiLanguageFromStorage(authStore) {
    const storedLanguage = safeLocalStorageGet('preferredLanguage')
    const hasLanguageInStore = Boolean(authStore.user?.data?.uiLanguage)

    if (!hasLanguageInStore && storedLanguage && authStore.user?.data) {
        authStore.user.data.uiLanguage = storedLanguage
    }

    return { storedLanguage, hasLanguageInStore }
}

const router = createRouter({
    history: createWebHistory(import.meta.env.BASE_URL),
    routes: [
        // Root route - Authentication page
        {
            path: '/',
            name: 'auth',
            component: Home,
            meta: {
                requiresAuth: false,
                noAuth: true,
            },
            beforeEnter: (to, from, next) => {
                // Import auth store
                const authStore = useAuthStore()

                // If we're in the middle of logging out, always proceed to auth page
                if (authStore.state.isLoggingOut) {
                    console.log('Logout in progress, allowing auth page access')
                    authStore.state.isLoggingOut = false
                    next()
                    return
                }

                // NOTE: Email verification for creators is temporarily disabled
                // Uncomment the block below to re-enable
                // Check if user needs email verification (email/password auth only)
                // const currentUser = firebase.auth().currentUser
                // if (currentUser && !currentUser.emailVerified) {
                //     // Check if user authenticated with email/password (not OAuth)
                //     const usedEmailPassword = currentUser.providerData.some(
                //         (provider) => provider.providerId === 'password'
                //     )
                // }

                // Check if user is logged in and has backend data
                if (authStore.user && authStore.user.data) {
                    // Redirect to appropriate chats page based on user type
                    const userType = authStore.user.data.typeofuser
                    if (userType === 'Creator') {
                        next({ name: 'creator-chats' })
                    } else {
                        next({ name: 'client-chats' })
                    }
                } else {
                    // Not logged in, proceed to auth page
                    next()
                }
            },
        },
        // Email verification redirect handler
        {
            path: '/email-verification',
            name: 'email-verification',
            component: () => import('../views/Home.vue'),
            meta: {
                requiresAuth: false,
                noAuth: true,
            },
            beforeEnter: async (to, from, next) => {
                const authStore = useAuthStore()
                const currentUser = firebase.auth().currentUser

                // If no user, redirect to auth page
                if (!currentUser) {
                    console.log(
                        'No user on email-verification redirect, redirecting to auth'
                    )
                    next({ name: 'auth' })
                    return
                }

                // Reload user to get latest verification status
                try {
                    await authUtils.reloadUserAuthState()
                    const updatedUser = firebase.auth().currentUser

                    // NOTE: Email verification for creators is temporarily disabled
                    // Changed from: if (updatedUser && updatedUser.emailVerified)
                    // Check if email is verified (bypassed - treating all as verified)
                    if (updatedUser) {
                        // Fetch fresh user data from backend
                        const UserService = (
                            await import('@/services/api/user.service.js')
                        ).default
                        const user = await UserService.getCurrentUser()

                        if (user && user.status === 200 && user.data) {
                            authStore.user.data = user.data

                            // Wait for app initialization
                            await waitForAppInitialization(authStore)

                            // Determine if this is a new user or existing user without setup
                            const isNewUser = !user.data.finishedSetup

                            // For new users OR users who haven't finished setup
                            if (isNewUser || !user.data.finishedSetup) {
                                next({ name: 'account-creation' })
                                return
                            }

                            // For existing users who have finished setup
                            const userType = user.data.typeofuser
                            if (user.data?.isSystemUser) {
                                next({ name: 'client-chats' })
                                return
                            }
                            if (userType === 'Creator') {
                                next({ name: 'creator-chats' })
                                return
                            } else if (userType === 'Client') {
                                // Check for pending creator conversation (existing client login after preview)
                                if (targetCreatorId && targetCreatorUsername) {
                                    console.log(
                                        'Email verification: Creating conversation for existing client'
                                    )
                                    try {
                                        const conversationStore = (
                                            await import(
                                                '@/stores/conversation.store'
                                            )
                                        ).useConversationStore()

                                        // Pre-populate usersMap with creator data from clientIntent if available
                                        if (
                                            targetCreator &&
                                            !conversationStore.state.usersMap[
                                            targetCreatorId
                                            ]
                                        ) {
                                            conversationStore.state.usersMap[
                                                targetCreatorId
                                            ] = targetCreator
                                            console.log(
                                                'Email verification (existing client): Pre-populated usersMap with creator data'
                                            )
                                        }

                                        await conversationStore.findOrCreateConversationWithUser(
                                            targetCreatorId,
                                            targetCreatorUsername
                                        )

                                        // Clean up creator info
                                        clearTargetCreator()
                                    } catch (error) {
                                        console.error(
                                            'Failed to create conversation in email verification:',
                                            error
                                        )
                                    }
                                }
                                next({ name: 'client-chats' })
                                return
                            } else if (userType === 'Admin') {
                                next({ name: 'admin-decaf-payouts' })
                                return
                            }
                        }

                        // Fallback: redirect to auth if we can't determine route
                        next({ name: 'auth' })
                    } else {
                        // Email not verified yet, show auth page with verification flow
                        next({ name: 'auth' })
                    }
                } catch (error) {
                    console.error(
                        'Error handling email verification redirect:',
                        error
                    )
                    next({ name: 'auth' })
                }
            },
        },
        // Auth redirect handler - Firebase redirects here after email verification
        {
            path: '/auth',
            name: 'auth-redirect',
            redirect: { name: 'email-verification' },
            meta: {
                requiresAuth: false,
                noAuth: true,
            },
        },
        // Landing page
        {
            path: '/category/nsfw',
            name: 'nsfw',
            component: () => import('../views/LandingPage.vue'),
            meta: {
                requiresAuth: false,
                noAuth: true,
            },
        },
        // escorts
        {
            path: '/escorts',
            name: 'escorts-home',
            component: () => import('../views/EscortsHome.vue'),
            meta: {
                view: 'escorts',
                noAuth: true,
            },
        },
        // sextpanther
        {
            path: '/sextpanther',
            name: 'sextpanther-home',
            component: () => import('../views/SextpantherHome.vue'),
            meta: {
                view: 'creator',
                noAuth: true,
            },
            beforeEnter: (to, from, next) => {
                const authStore = useAuthStore()

                if (authStore.user?.data && authStore.user.data.finishedSetup) {
                    if (authStore.user.data.isSystemUser) {
                        next({ name: 'client-chats' })
                        return
                    }
                    if (authStore.user.data.typeofuser === 'Client') {
                        next({ name: 'client-chats' })
                        return
                    }
                    if (authStore.user.data.typeofuser === 'Creator') {
                        next({ name: 'creator-chats' })
                        return
                    }
                }

                next()
            },
        },
        // mistresses onboarding flow
        {
            path: '/mistresses',
            name: 'mistresses-flow',
            component: () => import('../views/MistressesFlow.vue'),
            meta: {
                noAuth: true,
            },
        },
        //creator
        {
            path: '/creator',
            name: 'creator-home',
            component: () => import('../views/CreatorHomeNew.vue'),
            meta: {
                view: 'creator',
                noAuth: true,
            },
            beforeEnter: (to, from, next) => {
                const authStore = useAuthStore()

                // Check if user is logged in and has finished setup
                if (authStore.user?.data && authStore.user.data.finishedSetup) {
                    if (authStore.user.data.isSystemUser) {
                        next({ name: 'client-chats' })
                        return
                    }
                    // If user is a Client, redirect to client-chats
                    if (authStore.user.data.typeofuser === 'Client') {
                        console.log(
                            'Client user visiting creator page, redirecting to client-chats'
                        )
                        next({ name: 'client-chats' })
                        return
                    }

                    // If user is a Creator, redirect to creator-chats
                    if (authStore.user.data.typeofuser === 'Creator') {
                        next({ name: 'creator-chats' })
                        return
                    }
                }

                // Otherwise, proceed to creator home
                next()
            },
        },
        {
            path: '/dommes',
            name: 'domme-home',
            component: () => import('../views/DommeHome.vue'),
            meta: {
                view: 'creator',
                noAuth: true,
            },
            beforeEnter: (to, from, next) => {
                const authStore = useAuthStore()

                if (authStore.user?.data && authStore.user.data.finishedSetup) {
                    if (authStore.user.data.isSystemUser) {
                        next({ name: 'client-chats' })
                        return
                    }
                    if (authStore.user.data.typeofuser === 'Client') {
                        next({ name: 'client-chats' })
                        return
                    }
                    if (authStore.user.data.typeofuser === 'Creator') {
                        next({ name: 'creator-chats' })
                        return
                    }
                }

                next()
            },
        },
        {
            path: '/creator/pwa-setup',
            name: 'creator-pwa-setup',
            component: () => import('../views/CreatorPWASetup.vue'),
            meta: {
                view: 'creator',
            },
            beforeEnter: (to, from, next) => {
                const authStore = useAuthStore()

                // Check if user is logged in
                if (!authStore.user?.uid) {
                    next({ name: 'auth' })
                    return
                }

                syncUiLanguageFromStorage(authStore)

                next()
            },
        },
        {
            path: '/creator/profile-onboarding',
            name: 'creator-profile-onboarding',
            component: () => import('../views/CreatorProfileOnboarding.vue'),
            meta: {
                view: 'creator',
            },
            redirect: {
                name: 'creator-profile-onboarding-stage',
                params: { stage: 'need-display-name' },
            },
            children: [
                {
                    path: ':stage',
                    name: 'creator-profile-onboarding-stage',
                    component: () =>
                        import(
                            '../views/onboarding/OnboardingStageWrapper.vue'
                        ),
                },
            ],
        },
        // Client profile setup (name + chat style)
        {
            path: '/client/setup',
            name: 'client-profile-setup',
            component: () => import('../views/ClientProfileSetup.vue'),
            meta: {
                view: 'client',
            },
            redirect: {
                name: 'client-profile-setup-stage',
                params: { stage: 'name' },
            },
            children: [
                {
                    path: ':stage',
                    name: 'client-profile-setup-stage',
                    component: () =>
                        import(
                            '../views/onboarding/ClientProfileSetupStageWrapper.vue'
                        ),
                },
            ],
        },
        // Pre-chat setup (vibe + specific desire) for new conversations
        {
            path: '/client/pre-chat',
            name: 'client-pre-chat',
            component: () => import('../views/PreChatSetup.vue'),
            meta: {
                view: 'client',
            },
            redirect: {
                name: 'client-pre-chat-stage',
                params: { stage: 'vibe' },
            },
            children: [
                {
                    path: ':stage',
                    name: 'client-pre-chat-stage',
                    component: () =>
                        import(
                            '../views/onboarding/PreChatSetupStageWrapper.vue'
                        ),
                },
            ],
        },
        // Session booking setup
        {
            path: '/client/booking',
            name: 'client-booking',
            component: () => import('../views/BookingSetup.vue'),
            meta: {
                view: 'client',
            },
            redirect: {
                name: 'client-booking-stage',
                params: { stage: 'practices' },
            },
            children: [
                {
                    path: ':stage',
                    name: 'client-booking-stage',
                    component: () =>
                        import('../views/onboarding/BookingSetupStageWrapper.vue'),
                },
            ],
        },
        // Session call (scheduled video/voice via booking)
        {
            path: '/session/:bookingId',
            name: 'session-call',
            component: () => import('../views/SessionCall.vue'),
            meta: {
                view: 'client',
            },
        },
        {
            path: '/creator/chats',
            name: 'creator-chats',
            component: () => import('../views/Chats.vue'),
            meta: {
                view: 'creator',
                keepAlive: true,
            },
            beforeEnter: async (to, from, next) => {
                // If push-notification appended ?senderId, open that conversation first
                if (to.query.senderId) {
                    try {
                        const { useConversationStore } = await import(
                            '@/stores/conversation.store'
                        )
                        const convStore = useConversationStore()
                        await convStore.findOrCreateConversationWithUser(
                            to.query.senderId,
                            null
                        )
                    } catch (err) {
                        console.error(
                            'Failed to pre-open conversation from senderId',
                            err
                        )
                    }
                    const cleanQuery = { ...to.query }
                    delete cleanQuery.senderId
                    return next({ ...to, query: cleanQuery })
                }

                const authStore = useAuthStore()

                // Check if user has finished setup
                if (
                    authStore.user?.data &&
                    !authStore.user.data.finishedSetup
                ) {
                    console.log(
                        'Creator has not finished setup, redirecting to account creation'
                    )
                    next({ name: 'account-creation' })
                    return
                }

                next()
            },
        },
        {
            path: '/chats',
            name: 'chats-redirect',
            beforeEnter: async (to, from, next) => {
                const authStore = useAuthStore()

                if (!authStore.user?.data) {
                    return next({ name: 'auth' })
                }

                if (authStore.user.data.typeofuser === 'Creator') {
                    return next({ name: 'creator-chats', query: to.query })
                }

                return next({ name: 'client-chats', query: to.query })
            },
        },
        {
            path: '/creator/my-account',
            name: 'creator-my-account',
            component: () => import('../views/MyAccount.vue'),
            meta: {
                view: 'creator',
            },
        },
        {
            path: '/creator/my-services',
            name: 'creator-my-services',
            component: () => import('../views/MyServices.vue'),
            meta: {
                view: 'creator',
            },
        },
        {
            path: '/creator/my-money',
            name: 'creator-my-money',
            component: () => import('../views/Creator/MyTransactions.vue'),
            meta: {
                view: 'creator',
            },
        },
        {
            path: '/creator/my-payouts',
            name: 'creator-my-payouts',
            component: () => import('../views/Creator/MyPayouts.vue'),
            meta: {
                view: 'creator',
            },
        },
        {
            path: '/creator/my-content',
            name: 'creator-my-content',
            component: () => import('../views/MyContent.vue'),
            meta: {
                view: 'creator',
            },
        },

        {
            path: '/language-selection',
            name: 'language-selection',
            component: () => import('../views/LanguageSelection.vue'),
            meta: {
                view: 'client',
            },
            beforeEnter: (to, from, next) => {
                const authStore = useAuthStore()

                if (!authStore.user?.uid) {
                    next({ name: 'auth' })
                    return
                }

                next()
            },
        },

        {
            path: '/account-creation',
            name: 'account-creation',
            component: () => import('../views/AccountCreation.vue'),
            meta: {
                view: 'client', // Default to client view
            },
            beforeEnter: async (to, from, next) => {
                const authStore = useAuthStore()

                // Check if user is logged in
                if (!authStore.user?.uid) {
                    console.log(
                        'No user on account-creation page, redirecting to auth'
                    )
                    next({ name: 'auth' })
                    return
                }

                const { storedLanguage, hasLanguageInStore } =
                    syncUiLanguageFromStorage(authStore)
                let resolvedLanguage = storedLanguage

                const forceClientSignup = safeLocalStorageGet('forceClientSignup')
                const isForceClientSignup = forceClientSignup === 'client'
                if (isForceClientSignup) {
                    const userType = authStore.user?.data?.typeofuser
                    if (authStore.user?.data?.isSystemUser) {
                        safeLocalStorageRemove('forceClientSignup')
                    } else if (userType !== 'Creator' && userType !== 'Admin') {
                        safeLocalStorageSet('signupIntent', 'client')
                    } else {
                        safeLocalStorageRemove('forceClientSignup')
                    }
                }

                // Check if user is already set up
                if (authStore.user?.data?.finishedSetup) {
                    if (authStore.user.data.isSystemUser) {
                        return next({ name: 'client-chats' })
                    }
                    // If it's a client, redirect to client-chats
                    if (authStore.user.data.typeofuser === 'Client') {
                        console.log(
                            'Client already set up, redirecting to client-chats'
                        )
                        return next({ name: 'client-chats' })
                    }

                    // If it's a creator, redirect to creator-chats
                    if (authStore.user.data.typeofuser === 'Creator') {
                        return next({ name: 'creator-chats' })
                    }
                }

                if (!resolvedLanguage && !hasLanguageInStore) {
                    const autoLocale = detectNavigatorLocale()
                    if (autoLocale) {
                        const saved = safeLocalStorageSet(
                            'preferredLanguage',
                            autoLocale
                        )
                        if (saved) {
                            try {
                                await loadLocaleResources(autoLocale)
                                if (authStore.user?.data) {
                                    authStore.user.data.uiLanguage = autoLocale
                                }
                                const UserService = (
                                    await import('@/services/api/user.service.js')
                                ).default
                                await UserService.updateUser({
                                    uiLanguage: autoLocale,
                                })
                                resolvedLanguage = autoLocale
                            } catch (error) {
                                console.warn(
                                    'Failed to auto-apply preferred language:',
                                    error
                                )
                            }
                        }
                    }
                }

                if (!resolvedLanguage && !hasLanguageInStore) {
                    return next({ name: 'language-selection' })
                }

                const { getSignupData } = await import(
                    '@/utils/accountUpgrade.js'
                )

                const signupData = getSignupData(
                    new URLSearchParams(window.location.search)
                )

                // No auto-upgrade - show AccountCreation page for manual selection
                next()
            },
        },
        //Client
        {
            path: '/client/chats',
            name: 'client-chats',
            component: () => import('../views/Chats.vue'),
            meta: {
                view: 'client',
                keepAlive: true,
                order: 0,
            },
            beforeEnter: async (to, from, next) => {
                if (to.query.senderId) {
                    try {
                        const { useConversationStore } = await import(
                            '@/stores/conversation.store'
                        )
                        const convStore = useConversationStore()
                        await convStore.findOrCreateConversationWithUser(
                            to.query.senderId,
                            null
                        )
                    } catch (err) {
                        console.error(
                            'Failed to pre-open conversation from senderId',
                            err
                        )
                    }
                    const cleanQuery = { ...to.query }
                    delete cleanQuery.senderId
                    return next({ ...to, query: cleanQuery })
                }

                const authStore = useAuthStore()

                // Check if user is logged in
                if (!authStore.user?.data) {
                    next({ name: 'auth' })
                    return
                }

                // Check if user has finished setup (Creators only need this)
                // Clients are exempt - they use lazy setup via conversation.store gate
                const userType = authStore.user?.data?.typeofuser
                if (
                    authStore.user?.data &&
                    !authStore.user.data.finishedSetup &&
                    userType !== 'Client'
                ) {
                    console.log(
                        'User has not finished setup, redirecting to account creation'
                    )
                    next({ name: 'account-creation' })
                    return
                }

                next()
            },
        },
        {
            path: '/client/my-account',
            name: 'client-my-account',
            component: () => import('../views/MyAccount.vue'),
            meta: {
                view: 'client',
            },
        },
        {
            path: '/client/my-content',
            name: 'client-my-content',
            component: () => import('../views/MyContent.vue'),
            meta: {
                view: 'client',
            },
        },
        {
            path: '/client/my-transactions',
            name: 'client-my-transactions',
            component: () => import('../views/Client/MyTransactions.vue'),
            meta: {
                view: 'client',
            },
        },
        {
            path: '/preview-chat',
            name: 'preview-chat',
            component: () => import('../views/PreviewChat.vue'),
            meta: {
                view: 'client',
                noAuth: true,
                requiresAuth: false,
            },
        },
        {
            path: '/client/profiles',
            name: 'client-profiles',
            component: () => import('../views/CreatorListing.vue'),
            meta: {
                view: 'client',
                noAuth: true,
                order: 1,
            },
        },
        {
            path: '/mistresses/profiles',
            name: 'mistresses-profiles',
            component: () => import('../views/MistressesListing.vue'),
            meta: {
                view: 'client',
                noAuth: true,
                order: 1,
            },
        },
        // Admin routes with shared layout
        {
            path: '/admin',
            component: () => import('../layouts/AdminLayout.vue'),
            meta: {
                view: 'admin',
                requiresAdmin: true,
                allowDesktop: true,
            },
            children: [
                {
                    path: '',
                    redirect: '/admin/creators',
                },
                {
                    path: 'current-calls',
                    name: 'admin-current-calls',
                    component: () => import('../views/Admin/CurrentCalls.vue'),
                },
                {
                    path: 'decaf-payouts',
                    name: 'admin-decaf-payouts',
                    component: () => import('../views/Admin/DecafPayouts.vue'),
                },
                {
                    path: 'creators',
                    name: 'admin-creators',
                    component: () => import('../views/Admin/Creators.vue'),
                },
                {
                    path: 'clients',
                    name: 'admin-clients',
                    component: () => import('../views/Admin/Clients.vue'),
                },
                {
                    path: 'conversations',
                    name: 'admin-conversations',
                    component: () => import('../views/Admin/Conversations.vue'),
                },
                {
                    path: 'transactions',
                    name: 'admin-transactions',
                    component: () => import('../views/Admin/Transactions.vue'),
                },
            ],
        },
        // Compliance route
        {
            path: '/compliance/takedown-monitoring',
            name: 'compliance',
            component: () => import('../views/Compliance.vue'),
            meta: {
                noAuth: true,
            },
        },
        //Legal
        {
            path: '/legal/terms-of-use',
            name: 'legal-terms-of-use',
            component: () => import('../views/Legal/TermsOfUse.vue'),
            meta: {
                view: 'legal',
                noAuth: true,
                allowDesktop: true,
            },
        },
        {
            path: '/legal/privacy-policy',
            name: 'legal-privacy-policy',
            component: () => import('../views/Legal/PrivacyPolicy.vue'),
            meta: {
                view: 'legal',
                noAuth: true,
                allowDesktop: true,
            },
        },
        {
            path: '/legal/custodian-records-and-age-verification',
            name: 'legal-custodian-records-and-age-verification',
            component: () =>
                import('../views/Legal/CustodianRecordsAndAgeVerification.vue'),
            meta: {
                view: 'legal',
                noAuth: true,
                allowDesktop: true,
            },
        },
        {
            path: '/legal/dmca-takedown-policy',
            name: 'legal-dmca-takedown-policy',
            component: () => import('../views/Legal/DMCATakedownPolicy.vue'),
            meta: {
                view: 'legal',
                noAuth: true,
                allowDesktop: true,
            },
        },
        {
            path: '/legal/vat-tax-section',
            name: 'legal-vat-tax-section',
            component: () => import('../views/Legal/VATTaxSection.vue'),
            meta: {
                view: 'legal',
                noAuth: true,
                allowDesktop: true,
            },
        },
        {
            path: '/legal/appeals-policy',
            name: 'legal-appeals-policy',
            component: () => import('../views/Legal/AppealsPolicy.vue'),
            meta: {
                view: 'legal',
                noAuth: true,
                allowDesktop: true,
            },
        },
        {
            path: '/legal/referral-program-terms',
            name: 'legal-referral-program-terms',
            component: () => import('../views/Legal/ReferralProgramTerms.vue'),
            meta: {
                view: 'legal',
                noAuth: true,
                allowDesktop: true,
            },
        },
        {
            path: '/legal/platform-to-business-terms',
            name: 'legal-platform-to-business-terms',
            component: () =>
                import('../views/Legal/PlattformToBusinessTerms.vue'),
            meta: {
                view: 'legal',
                noAuth: true,
                allowDesktop: true,
            },
        },
        {
            path: '/legal/complaints-policy',
            name: 'legal-complaints-policy',
            component: () => import('../views/Legal/ComplaintsPolicy.vue'),
            meta: {
                view: 'legal',
                noAuth: true,
                allowDesktop: true,
            },
        },
        {
            path: '/legal/fan-creator-agreement',
            name: 'legal-fan-creator-agreement',
            component: () => import('../views/Legal/FanCreatorAgreement.vue'),
            meta: {
                view: 'legal',
                noAuth: true,
                allowDesktop: true,
            },
        },
        // {
        //     path: '/legal/fan-creator-agreement-studio',
        //     name: 'legal-fan-creator-agreement-studio',
        //     component: () =>
        //         import('../views/Legal/FanCreatorAgreementStudio.vue'),
        //     meta: {
        //         view: 'legal',
        //         noAuth: true,
        //     },
        // },
        {
            path: '/legal/acceptable-use-policy',
            name: 'legal-acceptable-use-policy',
            component: () => import('../views/Legal/AcceptibleUsePolicy.vue'),
            meta: {
                view: 'legal',
                noAuth: true,
                allowDesktop: true,
            },
        },
        {
            path: '/legal/ai-features',
            name: 'legal-ai-features',
            component: () => import('../views/Legal/ai-features.vue'),
            meta: {
                view: 'legal',
                noAuth: true,
                allowDesktop: true,
            },
        },
        {
            path: '/legal/content-management-policy',
            name: 'legal-content-management-policy',
            component: () =>
                import('../views/Legal/ContentManagementPolicy.vue'),
            meta: {
                view: 'legal',
                noAuth: true,
                allowDesktop: true,
            },
        },
        {
            path: '/legal/chargeback-fraud-mitigation-policy',
            name: 'legal-chargeback-fraud-mitigation-policy',
            component: () =>
                import('../views/Legal/ChargebackFraudMitigationPolicy.vue'),
            meta: {
                view: 'legal',
                noAuth: true,
                allowDesktop: true,
            },
        },
        {
            path: '/legal/return-refund-policy',
            name: 'legal-return-refund-policy',
            component: () => import('../views/Legal/ReturnRefundPolicy.vue'),
            meta: {
                view: 'legal',
                noAuth: true,
                allowDesktop: true,
            },
        },
        {
            path: '/legal/responsible-use-and-spending-policy',
            name: 'legal-responsible-use-and-spending-policy',
            component: () =>
                import('../views/Legal/ResponsibleUseAndSpending.vue'),
            meta: {
                view: 'legal',
                noAuth: true,
                allowDesktop: true,
            },
        },
        {
            path: '/legal/session-recording-policy',
            name: 'legal-session-recording-policy',
            component: () => import('../views/Legal/SessionRecordingPolicy.vue'),
            meta: {
                view: 'legal',
                noAuth: true,
                allowDesktop: true,
            },
        },
        {
            path: '/legal/anti-modern-slavery-declaration',
            name: 'legal-anti-modern-slavery-declaration',
            component: () =>
                import('../views/Legal/AntiModernSlaveryDeclaration.vue'),
            meta: {
                view: 'legal',
                noAuth: true,
                allowDesktop: true,
            },
        },
        {
            path: '/legal/partners-agreement',
            name: 'legal-partners-agreement',
            component: () => import('../views/Legal/PartnersAgreement.vue'),
            meta: {
                view: 'legal',
                noAuth: true,
                allowDesktop: true,
            },
        },
        {
            path: '/legal/penalties',
            name: 'legal-penalties',
            component: () => import('../views/Legal/Penalties.vue'),
            meta: {
                view: 'legal',
                noAuth: true,
                allowDesktop: true,
            },
        },
        {
            path: '/legal/content-permission',
            name: 'legal-content-permission',
            component: () => import('../views/Legal/ContentPermission.vue'),
            meta: {
                view: 'legal',
                noAuth: true,
                allowDesktop: true,
            },
        },
        {
            path: '/legal/permiso-contenido',
            name: 'legal-permiso-contenido',
            component: () => import('../views/Legal/PermisoContenido.vue'),
            meta: {
                view: 'legal',
                noAuth: true,
                allowDesktop: true,
            },
        },
        {
            path: '/legal/creator-growth-services',
            redirect: '/legal/content-permission',
        },
        //Legal Agreements: superStudio
        // {
        //     path: '/legal/agreements/superStudio',
        //     name: 'legal-super-studio-agreement',
        //     component: () =>
        //         import('../views/Legal/agreements/superstudioAgreement.vue'),
        //     meta: {
        //         view: 'legal',
        //         noAuth: true,
        //     },
        // },
        //Legal Agreements: creator-studio
        {
            path: '/legal/agreements/creator-studio',
            name: 'legal-creator-studio-agreement',
            component: () =>
                import('../views/Legal/agreements/creatorStudioAgreement.vue'),
            meta: {
                view: 'legal',
                noAuth: true,
                allowDesktop: true,
            },
        },
        //Legal Agreements: creator
        {
            path: '/legal/agreements/creator',
            name: 'legal-creator-agreement',
            component: () =>
                import('../views/Legal/agreements/creatorAgreement.vue'),
            meta: {
                view: 'legal',
                noAuth: true,
                allowDesktop: true,
            },
        },
        //Partners
        {
            path: '/partners',
            name: 'partners',
            component: () => import('../views/Partners.vue'),
            meta: {
                view: 'partners',
                noAuth: true,
                allowDesktop: true,
            },
        },
        // Invest
        {
            path: '/invest',
            name: 'invest',
            component: () => import('../views/Invest.vue'),
            meta: {
                view: 'invest',
                noAuth: true,
                allowDesktop: true,
            },
        },
        // Advice (Calendly booking)
        {
            path: '/advice',
            name: 'advice',
            component: () => import('../views/Advice.vue'),
            meta: {
                view: 'advice',
                noAuth: true,
                allowDesktop: true,
            },
        },
        // Positions
        {
            path: '/positions/gma',
            name: 'positions-gma',
            component: () => import('../views/Positions/GMA.vue'),
            meta: {
                noAuth: true,
                allowDesktop: true,
            },
        },
        {
            path: '/positions/growth_marketer',
            name: 'positions-growth-marketer',
            component: () => import('../views/Positions/GrowthMarketer.vue'),
            meta: {
                noAuth: true,
                allowDesktop: true,
            },
        },
        // Workshop
        {
            path: '/workshop',
            name: 'workshop',
            component: () => import('../views/Workshop.vue'),
            meta: {
                view: 'workshop',
                noAuth: true,
                allowDesktop: true,
            },
        },
        // Workshop Presentation
        {
            path: '/presentations/workshop',
            name: 'presentations-workshop',
            component: () => import('../views/Presentations/Workshop.vue'),
            meta: {
                noAuth: true,
                allowDesktop: true,
            },
        },
        // Clean profile route - hides services and listing gallery
        {
            path: '/c/:slug',
            name: 'creator-profile-clean',
            component: () => import('../views/CreatorProfileShare.vue'),
            meta: {
                noAuth: true,
                order: 2,
                cleanPresentation: true,
            },
        },
        {
            path: '/:slug',
            name: 'creator-profile',
            component: () => import('../views/CreatorProfileShare.vue'),
            meta: {
                noAuth: true,
                order: 2,
            },
        },
        // Catch-all 404 route - must be last
        {
            path: '/:pathMatch(.*)*',
            name: 'not-found',
            component: () => import('../views/NotFound.vue'),
            meta: {
                noAuth: true,
            },
        },
    ],
})

router.onError((error) => {
    if (isStaleModuleImportError(error)) {
        try {
            console.warn(
                '[router] Detected stale module import. Forcing reload...',
                error?.message || error
            )
        } catch (_) { }
        forceReloadDueToStaleAssets({
            reason: 'router-chunk-load-failed',
        })
    }
})

// Track page views manually to bypass adblockers (which block the SDK autocapture)
// Restricted to once per session (acting as a "Session Start" / "Visit" event)
router.afterEach((to) => {
    try {
        const SESSION_TRACK_KEY = 'has_tracked_session_pageview'
        if (!sessionStorage.getItem(SESSION_TRACK_KEY)) {
            sessionStorage.setItem(SESSION_TRACK_KEY, 'true')
            trackEvent('Page View', {
                path: to.path,
                name: to.name,
                params: to.params,
                query: to.query,
            })
        }
    } catch (_) { }
})

export default router
